import { Component, ElementRef, HostListener, Input, OnChanges, OnInit, SimpleChanges, ViewChild, AfterViewChecked } from '@angular/core';
import { DestinationVideoService, DestinationConfig } from 'src/app/core/services/destination-video.service';

@Component({
  selector: 'app-assist-header-banner',
  templateUrl: './assist-header-banner.component.html',
  styleUrls: ['./assist-header-banner.component.scss']
})
export class AssistHeaderBannerComponent implements OnInit, OnChanges, AfterViewChecked {
  @ViewChild('bannerWindow') bannerWindow!: ElementRef<HTMLDivElement>;
  @ViewChild('bannerVideo') bannerVideoRef?: ElementRef<HTMLVideoElement>;
  @Input() destination?: string;

  public videoBlobUrl: string | null = null;
  public posterUrl: string = '';
  public candidateSources: Array<{ url: string; type: string }> = [];
  public isVideoLoaded: boolean = false;
  public isMobile: boolean = false;
  public currentDestination: DestinationConfig | null = null;

  public parallaxOffsetY: number = 0;
  public parallaxTransform: string = 'translate3d(0, 0, 0) scale(1.35)';

  private ticking: boolean = false;
  private currentLoadedKey: string = '';
  private videoPlayAttempted: boolean = false;

  constructor(private destinationVideoService: DestinationVideoService) { }

  ngOnInit(): void {
    this.isMobile = this.destinationVideoService.isMobileDevice();
    this.loadBannerVideo();

    // Initial parallax calculation
    setTimeout(() => {
      this.calculateParallax();
    }, 100);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['destination'] && !changes['destination'].firstChange) {
      this.currentLoadedKey = '';
      this.loadBannerVideo();
    }
  }

  @HostListener('window:scroll')
  onWindowScroll() {
    if (!this.ticking) {
      window.requestAnimationFrame(() => {
        this.calculateParallax();
        this.ticking = false;
      });
      this.ticking = true;
    }
  }

  @HostListener('window:resize')
  onResize() {
    const isNowMobile = this.destinationVideoService.isMobileDevice();
    if (isNowMobile !== this.isMobile) {
      this.isMobile = isNowMobile;
      this.currentLoadedKey = '';
      this.loadBannerVideo();
    }
    this.calculateParallax();
  }

  private async loadBannerVideo() {
    const destId = this.destination || this.destinationVideoService.getSelectedDestination() || 'global';
    const { destination, variant, primaryUrl, posterUrl, sources } =
      this.destinationVideoService.getVideoSources(destId);

    const loadKey = `${destination.id}_${variant}`;
    this.currentDestination = destination;
    this.posterUrl = posterUrl;
    this.candidateSources = sources;

    if (this.currentLoadedKey === loadKey && this.videoBlobUrl) {
      return;
    }

    this.currentLoadedKey = loadKey;
    this.isVideoLoaded = false;
    this.videoPlayAttempted = false;

    try {
      const blobUrl = await this.destinationVideoService.getCachedVideoBlobUrl(primaryUrl);
      this.videoBlobUrl = blobUrl;

      if (this.bannerVideoRef?.nativeElement) {
        const video = this.bannerVideoRef.nativeElement;
        video.src = blobUrl;
        video.load();
        video.play().catch(() => {});
      }
    } catch (err) {
      console.warn('Failed to load cached video blob for banner:', err);
      this.videoBlobUrl = null;
    }
  }

  onVideoCanPlay() {
    this.isVideoLoaded = true;
    if (this.bannerVideoRef?.nativeElement) {
      const video = this.bannerVideoRef.nativeElement;
      video.muted = true;
      video.play().catch(() => {});
    }
  }

  ngAfterViewChecked() {
    if (this.bannerVideoRef?.nativeElement && !this.videoPlayAttempted) {
      this.videoPlayAttempted = true;
      const video = this.bannerVideoRef.nativeElement;
      video.muted = true;
      video.play().catch(() => {});
    }
  }

  private calculateParallax() {
    if (typeof window === 'undefined') return;

    // Get current page scroll distance
    const scrollY = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
    
    // Smooth vertical scroll translation through window aperture
    const scrollFactor = Math.min(2.5, scrollY / 180);
    this.parallaxOffsetY = scrollFactor * -85;
    
    this.parallaxTransform = `translate3d(0, ${this.parallaxOffsetY}px, 0) scale(1.35)`;
  }
}




